import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatMAD, type OrderStatus, type PaymentStatus } from "@tarawa/shared";
import { api, ApiError } from "./api";

export interface Driver {
  id: string;
  fullName: string;
  email: string;
  role: "DRIVER";
}

export function useDriver() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["driver-me"],
    queryFn: async () => (await api<{ driver: Driver | null }>("/auth/me")).driver,
    staleTime: 60_000,
  });
  return { driver: q.data ?? null, isLoading: q.isLoading, setDriver: (d: Driver | null) => qc.setQueryData(["driver-me"], d) };
}

export interface Summary {
  available: number;
  accepted: number;
  inProgress: number;
  deliveredToday: number;
}

export interface OrderCard {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  createdAt: string;
  confirmedAt: string | null;
  customerName: string;
  addressLine: string;
  city: string;
  hasGps: boolean;
  itemCount: number;
  totalCents: number;
  codAmountCents: number;
  driverAssignedAt?: string | null;
  deliveredAt?: string | null;
  deliveryFailureReason?: string | null;
  paymentStatus?: PaymentStatus | null;
}

export interface MyOrders {
  accepted: OrderCard[];
  inProgress: OrderCard[];
  delivered: OrderCard[];
  failed: OrderCard[];
}

export interface OrderDetail {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  createdAt: string;
  driverAssignedAt: string | null;
  deliveredAt: string | null;
  deliveryFailedAt: string | null;
  deliveryFailureReason: string | null;
  customer: { name: string; phone: string };
  delivery: { addressLine: string; city: string; instructions: string | null; latitude: number | null; longitude: number | null };
  notes: string | null;
  items: {
    id: string;
    productName: string;
    quantity: number;
    unitPriceCents: number;
    lineTotalCents: number;
    /** Pack lines: what to put in the bag for each pack */
    packContents?: { name: string; quantity: number }[] | null;
  }[];
  subtotalCents: number;
  deliveryFeeCents: number;
  discountCents: number;
  totalCents: number;
  payment: { method: string; status: PaymentStatus; amountCents: number; collectedAmountCents: number | null; collectedAt: string | null } | null;
}

/** Delivery state from the driver's point of view (the order is already assigned to them). */
export function deliveryLabel(status: OrderStatus): string {
  switch (status) {
    case "CONFIRMED":
    case "PREPARING":
      return "Acceptée";
    case "OUT_FOR_DELIVERY":
      return "En cours de livraison";
    case "DELIVERED":
      return "Livrée";
    case "DELIVERY_FAILED":
      return "Échec de livraison";
    case "CANCELLED":
      return "Annulée";
    default:
      return "En attente";
  }
}

export const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  PENDING: "À encaisser",
  PAID: "Payé",
  FAILED: "Non encaissé",
  CANCELLED: "Annulé",
};

const ERRORS: Record<string, string> = {
  NETWORK: "Connexion au serveur impossible.",
  INVALID_CREDENTIALS: "Email ou mot de passe incorrect.",
  LOGIN_LOCKED: "Trop de tentatives. Réessayez dans quelques minutes.",
  ACCOUNT_DISABLED: "Ce compte est désactivé.",
  DRIVER_ONLY: "Ce compte n'est pas un compte livreur.",
  RATE_LIMITED: "Trop de requêtes. Réessayez dans un instant.",
  UNAUTHENTICATED: "Session expirée. Reconnectez-vous.",
  ORDER_ALREADY_TAKEN: "Cette commande a déjà été prise par un autre livreur.",
  ORDER_NOT_AVAILABLE: "Cette commande n'est plus disponible.",
  DRIVER_BUSY: "Terminez votre livraison en cours avant d'accepter une autre commande.",
  ORDER_NOT_FOUND: "Commande introuvable ou non attribuée à votre compte.",
  NOT_FOUND: "Commande introuvable.",
  DELIVERY_ALREADY_STARTED: "La livraison a déjà commencé.",
  DELIVERY_ALREADY_COMPLETED: "Cette livraison est déjà confirmée.",
  PAYMENT_ALREADY_RECORDED: "Le paiement est déjà enregistré.",
  INVALID_STATUS_TRANSITION: "Action impossible dans l'état actuel de la commande.",
  INVALID_PAYMENT_TRANSITION: "Le paiement ne peut pas être enregistré dans son état actuel.",
  VALIDATION_ERROR: "Veuillez vérifier les informations saisies.",
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "AMOUNT_MISMATCH" && typeof err.details.expectedCents === "number") {
      return `Le montant encaissé doit être exactement ${formatMAD(err.details.expectedCents)}.`;
    }
    return ERRORS[err.code] ?? `Erreur (${err.code})`;
  }
  return "Une erreur est survenue.";
}

export function fmtTime(iso: string | null | undefined, withDate = true): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat("fr-MA", {
    ...(withDate ? { day: "2-digit", month: "short" } : {}),
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}
